import { z } from 'zod';
/**
 * Base58 (Bitcoin alphabet) utilities for Solana addresses and signatures.
 * Validation decodes fully so length checks are exact (a 32-byte key can be 32–44 chars).
 */
export class Base58 {
    static ALPHABET = '123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz';
    static INDEX = new Map([...Base58.ALPHABET].map((char, index) => [char, index]));
    /** Decodes a base58 string into bytes, or returns `null` when it contains invalid characters. */
    static decode(value) {
        if (value.length === 0)
            return null;
        // Leading '1' characters encode leading zero bytes.
        let zeros = 0;
        while (zeros < value.length && value[zeros] === '1')
            zeros++;
        // log(58) / log(256) ≈ 0.7322 → upper bound on decoded size.
        const buffer = new Uint8Array(Math.ceil((value.length - zeros) * 0.7322) + 1);
        let length = 0;
        for (let i = zeros; i < value.length; i++) {
            let carry = Base58.INDEX.get(value[i]);
            if (carry === undefined)
                return null;
            let processed = 0;
            for (let k = buffer.length - 1; (carry !== 0 || processed < length) && k >= 0; k--, processed++) {
                carry += 58 * (buffer[k] ?? 0);
                buffer[k] = carry % 256;
                carry = Math.floor(carry / 256);
            }
            length = processed;
        }
        let start = buffer.length - length;
        while (start < buffer.length && buffer[start] === 0)
            start++;
        const out = new Uint8Array(zeros + (buffer.length - start));
        out.set(buffer.subarray(start), zeros);
        return out;
    }
    /** True when `value` decodes to exactly `byteLength` bytes. */
    static isOfLength(value, byteLength) {
        const decoded = Base58.decode(value);
        return decoded !== null && decoded.length === byteLength;
    }
}
/** A Solana public key / address (32 bytes, base58). */
export const zAddress = z
    .string()
    .trim()
    .min(32)
    .max(44)
    .refine((value) => Base58.isOfLength(value, 32), { message: 'Not a valid Solana address' });
/** A transaction signature (64 bytes, base58). */
export const zSignature = z
    .string()
    .trim()
    .min(64)
    .max(90)
    .refine((value) => Base58.isOfLength(value, 64), { message: 'Not a valid transaction signature' });
/** MongoDB ObjectId as 24 hex chars. */
export const zObjectId = z.string().regex(/^[a-f0-9]{24}$/i, 'Not a valid id');
const U64_MAX = 18446744073709551615n;
/** An exact unsigned 64-bit integer carried as a decimal string (JSON-safe; parse with `BigInt`). */
export const zU64String = z
    .string()
    .regex(/^\d{1,20}$/, 'Must be a non-negative integer string')
    .refine((value) => BigInt(value) <= U64_MAX, { message: 'Exceeds u64' });
/** Lamports (1 SOL = 1_000_000_000 lamports) as an exact decimal string. */
export const zLamports = zU64String;
/** Basis points 0..=10_000. */
export const zBps = z.number().int().min(0).max(10_000);
/** A percentage 0..=100 (fractional allowed). */
export const zPercent = z.number().min(0).max(100);
/** Cursor pagination request shared by all list endpoints. */
export const zCursorQuery = z.object({
    cursor: z.string().max(200).optional(),
    limit: z.coerce.number().int().min(1).max(200).default(50),
});
/** Error envelope returned by every API error response. */
export const zApiError = z.object({
    error: z.object({
        code: z.string(),
        message: z.string(),
        details: z.unknown().optional(),
    }),
});
/** Stable, machine-readable API error codes. */
export const ApiErrorCode = {
    Validation: 'validation_failed',
    Unauthorized: 'unauthorized',
    Forbidden: 'forbidden',
    NotFound: 'not_found',
    Conflict: 'conflict',
    RateLimited: 'rate_limited',
    StepUpRequired: 'step_up_required',
    EngineUnavailable: 'engine_unavailable',
    TransactionFailed: 'transaction_failed',
    /** A wallet's stored key couldn't be unlocked (server-side problem; nothing was signed). */
    KeyUnavailable: 'key_unavailable',
    /**
     * An administrator deactivated the signed-in account (403): every request is refused except
     * reading `GET /api/me` and signing out, until the account is activated again.
     */
    AccountDeactivated: 'account_deactivated',
    Upstream: 'upstream_error',
    Internal: 'internal_error',
};
//# sourceMappingURL=primitives.js.map